import { Page } from '@hud/web-components/Layout/Page';
import useRules from '../../../hooks/useRules';
import { Rule } from '@hud/rules-dtos/api-rules.dtos';
import { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  SortingState,
  PaginationState,
  createColumnHelper,
} from '@tanstack/react-table';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import { Search } from '@hud/web-components/Search/Search';
import TableContainer from '@hud/web-components/Container/TableContainer';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import RunningStatus from '@hud/web-components/RunningStatus/RunningStatus';
import { TextContainer } from './HeadsUpRulesPage.styles';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';

export default function HeadsUpRulesPage() {
  const rulesQuery = useRules();
  return (
    <Page header="Rules">
      {/* if error show error message */}
      {rulesQuery.error && <div>Error: {rulesQuery.error.message}</div>}
      <RulesTable
        rules={rulesQuery.data?.rules ?? []}
        loading={rulesQuery.isLoading}
      />
    </Page>
  );
}

function RulesTable({ rules, loading }: { rules: Rule[]; loading: boolean }) {
  const globalFilters = useGlobalQueryString();
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'enabled', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const columnHelper = createColumnHelper<Rule>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ row }) => {
          return (
            <LinkCell
              to={`/heads-up/rules/${row.original.id}?${globalFilters}`}
            >
              {row.original.name}
            </LinkCell>
          );
        },
        size: 180,
        maxSize: 450,
        minSize: 100,
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('shortDescription', {
        header: 'Description',
        cell: ({ row }) => {
          return <TextContainer>{row.original.shortDescription}</TextContainer>;
        },
        size: 400,
        maxSize: 500,
        minSize: 300,
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('enabled', {
        header: 'Status',
        cell: ({ row }) => {
          return (
            <RunningStatus
              status={row.original.enabledServices > 0 ? 'enabled' : 'disabled'}
              hideLabelOnOverflow={false}
              text={
                row.original.enabledServices > 0
                  ? `${row.original.enabledServices}/${row.original.totalServices} services`
                  : ''
              }
            />
             
          );
        },
        maxSize: 180,
        size: 150,
        minSize: 65,
        enableResizing: true,
        sortingFn: (rowA, _rowB, _columnId) => {
          return rowA.original.enabled ? 1 : -1;
        },
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.display({
        header: 'Actions',
        minSize: 100,
        maxSize: 100,
        cell: ({ row }) => {
          return (
            <LinkCell
              to={`/heads-up/rules/${row.original.id}?${globalFilters}`}
            >
              View / Edit
            </LinkCell>
          );
        },
        enableResizing: false,
        meta: {
          disableAutoFit: true,
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rules ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,

    state: {
      pagination,
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
  });

  return (
    <TableContainer.Container
      title={
        loading ? (
          <Skeleton width={140} />
        ) : (
          `Showing ${table.getPrePaginationRowModel().rows.length}/${rules.length}`
        )
      }
    >
      <TableContainer.Header>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
        {loading ? (
          <Skeleton width={100} />
        ) : rules ? (
          <ItemCounter
            total={rules.length}
            filtered={table.getPrePaginationRowModel().rows.length}
          />
        ) : null}
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Rules Table"
          loading={loading}
          aria-busy={loading}
        />
      </HorizontalContainer>
    </TableContainer.Container>
  );
}
